import React from "react";
import { Helmet } from "react-helmet";
import { applyHostBranding, isBookingBrandHost, isCareBrandHost } from "../../util/brandDomains";

const DEFAULT_TITLE = "Plany365 | Booking, Care, Jobs, and Business Tools";
const DEFAULT_DESCRIPTION =
  "Create booking pages, share business links, find care, post jobs, and manage customer-facing pages with Plany365.";
const CARE_TITLE = "Care.Plany365 | Care, eMAR, Booking, Jobs";
const CARE_DESCRIPTION =
  "Run care operations with Plany365 Care: care provider listings, eMAR, booking, caregiver jobs, referrals, tours, and availability.";
const TIMELY365_TITLE = "Timely365 | Booking, Save Events From Messages, Posters, and Screenshots";
const TIMELY365_DESCRIPTION =
  "Timely365 handles booking and turns event messages, posters, screenshots, and address photos into calendar entries and saved places.";

const toAbsoluteUrl = (value) => {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  if (typeof window === "undefined") return value;

  const origin = window.location.origin.replace(/\/$/, "");
  const path = String(value).startsWith("/") ? value : `/${value}`;
  return `${origin}${path}`;
};

const normalizeDescription = (value) => {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return DEFAULT_DESCRIPTION;
  return text.length > 160 ? `${text.slice(0, 157)}...` : text;
};

const getDefaultMeta = () => {
  if (isBookingBrandHost()) {
    return {
      title: TIMELY365_TITLE,
      description: TIMELY365_DESCRIPTION,
      siteName: "Timely365",
    };
  }
  if (isCareBrandHost()) {
    return {
      title: CARE_TITLE,
      description: CARE_DESCRIPTION,
      siteName: "Care.Plany365",
    };
  }
  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    siteName: "Plany365",
  };
};

const SeoMeta = ({
  title,
  description,
  image = "/og-preview.png",
  imageType = "image/png",
  imageWidth = 1200,
  imageHeight = 630,
  url,
  type = "website",
  siteName = "Plany365",
  twitterCard = "summary_large_image",
}) => {
  const defaultMeta = getDefaultMeta();
  const normalizedTitle = title ? applyHostBranding(title) : defaultMeta.title;
  const normalizedDescription = description
    ? applyHostBranding(normalizeDescription(description))
    : normalizeDescription(defaultMeta.description);
  const normalizedImage = toAbsoluteUrl(image || "/og-preview.png");
  const normalizedUrl =
    toAbsoluteUrl(url) ||
    (typeof window !== "undefined" ? window.location.href : "https://plany365.com/");
  const normalizedSiteName = siteName === "Plany365" ? defaultMeta.siteName : applyHostBranding(siteName);

  return (
    <Helmet>
      <title>{normalizedTitle}</title>
      <meta name="description" content={normalizedDescription} />
      <link rel="canonical" href={normalizedUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={normalizedSiteName} />
      <meta property="og:url" content={normalizedUrl} />
      <meta property="og:title" content={normalizedTitle} />
      <meta property="og:description" content={normalizedDescription} />
      <meta property="og:image" content={normalizedImage} />
      <meta property="og:image:secure_url" content={normalizedImage} />
      <meta property="og:image:type" content={imageType} />
      {imageWidth != null ? <meta property="og:image:width" content={String(imageWidth)} /> : null}
      {imageHeight != null ? <meta property="og:image:height" content={String(imageHeight)} /> : null}

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={normalizedTitle} />
      <meta name="twitter:description" content={normalizedDescription} />
      <meta name="twitter:image" content={normalizedImage} />
    </Helmet>
  );
};

export default SeoMeta;
